import { createAsyncThunk } from '@reduxjs/toolkit'
import { WorkingStepsController } from 'controllers/WorkingStepsController'
import { AssemblyHeaderRow, BomItemType } from 'model/Project/BoMItemRow'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import {
  BoMItemIdAndActivityId,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { ShowException } from 'store/Application/appActions'
import { RootAppState } from 'store/configureStore'
import { bomItemSelector } from '../selectors/bomItemSelector'

export const removeWorkingStepFromAllParentFilteredRows = createAsyncThunk<
  Promise<void>,
  {
    bomItemPointer: BomItemPointer
    workingStepType: WorkingStepType
    activityId: string
    resourceId?: string
  },
  { state: RootAppState }
>(
  'bom/removeWorkingStepFromAllParentFilteredRows',
  async ({ bomItemPointer, workingStepType, activityId, resourceId }, thunkAPI) => {
    const bomItem = bomItemSelector(thunkAPI.getState(), bomItemPointer)
    const projectId = thunkAPI.getState().project.activeProject.id
    const workingStepsController = new WorkingStepsController()
    const matchesActivity = (activity) =>
      activity.primaryWorkingStep === workingStepType &&
      (!resourceId || activity.resource?.id === resourceId)

    try {
      switch (bomItem?.type) {
        case BomItemType.assemblyType: {
          let pointersToChange: Array<BomItemPointer> = []

          const assemblyPointer = {
            id: bomItem.id,
            type: BomItemType.assemblyType,
          }

          const assembly = bomItemSelector(
            thunkAPI.getState(),
            assemblyPointer,
          ) as AssemblyHeaderRow

          pointersToChange.push(assemblyPointer)
          pointersToChange = pointersToChange.concat(
            assembly.filteredPartTypePointers || [],
          )

          // Build boMItemAndActivityIds by finding matching activities on each bomItem
          const boMItemAndActivityIds: BoMItemIdAndActivityId[] =
            pointersToChange.flatMap((pointer) => {
              const item = bomItemSelector(thunkAPI.getState(), pointer)
              const activities =
                item?.type === BomItemType.assemblyType ||
                item?.type === BomItemType.assemblyInstance
                  ? (item as AssemblyHeaderRow).assemblyActivities
                  : 'activities' in item
                    ? item.activities
                    : []
              return (activities?.filter(matchesActivity) || []).map(
                (matchingActivity) => ({
                  boMItemId: pointer.id,
                  activityId: matchingActivity.id,
                }),
              )
            })

          return await workingStepsController.DeleteWorkingSteps(
            projectId,
            boMItemAndActivityIds,
          )
        }
        case BomItemType.partType:
          const parentBomItem = bomItemSelector(
            thunkAPI.getState(),
            bomItem?.parentBomItemPointer,
          )
          if (
            parentBomItem.type === BomItemType.assemblyType ||
            parentBomItem.type === BomItemType.materialHeader
          ) {
            const boMItemAndActivityIds: BoMItemIdAndActivityId[] =
              parentBomItem.filteredPartTypePointers.flatMap((pointer) => {
                const item = bomItemSelector(thunkAPI.getState(), pointer)
                const activities = 'activities' in item ? item.activities : []
                return (activities?.filter(matchesActivity) || []).map(
                  (matchingActivity) => ({
                    boMItemId: pointer.id,
                    activityId: matchingActivity.id,
                  }),
                )
              })

            return await workingStepsController.DeleteWorkingSteps(
              projectId,
              boMItemAndActivityIds,
            )
          }
          break
        default:
          throw 'removeWorkingStepFromAllParentFilteredRows needs a bomItem of type Row'
      }
    } catch (ex) {
      ShowException('project', ex)
      throw ex
    }
  },
)

export const applyWorkingStepToAllParentFilteredRows = createAsyncThunk<
  Promise<void>,
  {
    bomItemPointer: BomItemPointer
    activityId: string
  },
  { state: RootAppState }
>(
  'bom/applyWorkingStepToAllParentFilteredRows',
  async ({ bomItemPointer, activityId }, thunkAPI) => {
    const bomItem = bomItemSelector(thunkAPI.getState(), bomItemPointer)

    try {
      if (bomItem?.type === BomItemType.partType) {
        const parentBomItem = bomItemSelector(
          thunkAPI.getState(),
          bomItem?.parentBomItemPointer,
        )
        if (
          parentBomItem.type === BomItemType.assemblyType ||
          parentBomItem.type === BomItemType.materialHeader
        ) {
          const workingStepsController = new WorkingStepsController()

          return await workingStepsController.CopyWorkingStepsToOtherParts(
            bomItemPointer,
            activityId,
            parentBomItem.filteredPartTypePointers.filter(
              (x) => x.id !== bomItemPointer.id,
            ),
          )
        }
      } else {
        throw 'ApplyWorkingStepToAll needs a bomItem of type Row'
      }
    } catch (ex) {
      ShowException('project', ex)
      throw ex
    }
  },
)
